import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { areaAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage } from '../Common/admin';

const AreaCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    areaCode: '',
    areaNameAr: '',
    areaNameEng: '',
    countryId: '',
    isActive: true,
  });
  const [countries, setCountries] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadCountries = useCallback(async () => {
    try {
      const response = await areaAPI.getCountries();
      setCountries(response.data || []);
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  }, []);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.areaCode.trim()) {
      newErrors.areaCode = t('area.create.errors.areaCodeRequired');
    }
    if (!formData.areaNameAr.trim()) {
      newErrors.areaNameAr = t('area.create.errors.nameArRequired');
    }
    if (!formData.areaNameEng.trim()) {
      newErrors.areaNameEng = t('area.create.errors.nameEngRequired');
    }
    if (!formData.countryId) {
      newErrors.countryId = t('area.create.errors.countryRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await areaAPI.create({ ...formData, countryId: parseInt(formData.countryId) });
      setShowSuccessModal(true);
      setTimeout(() => navigate('/area'), 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t('area.create.messages.createError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  const textField = (name, label, maxLength) => (
    <div className="ad-form-field">
      <label className="ad-label ad-label-required" htmlFor={name}>
        {label}
      </label>
      <input
        id={name}
        type="text"
        name={name}
        className={`ad-input ${errors[name] ? 'ad-input-error' : ''}`}
        value={formData[name]}
        onChange={handleChange}
        maxLength={maxLength}
      />
      {errors[name] && <span className="ad-error-text">{errors[name]}</span>}
    </div>
  );

  return (
    <AdminPage
      title={t('area.create.title')}
      headerActions={
        <button type="button" className="ad-btn" onClick={() => navigate('/area')}>
          <i className="fas fa-arrow-right"></i>
          {t('area.create.backToList')}
        </button>
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="ad-form">
          <div className="ad-form-grid">
            {textField('areaCode', t('area.create.fields.areaCode'), 20)}
            {textField('areaNameAr', t('area.create.fields.nameAr'), 100)}
            {textField('areaNameEng', t('area.create.fields.nameEng'), 100)}

            <div className="ad-form-field">
              <label className="ad-label ad-label-required" htmlFor="countryId">
                {t('area.create.fields.country')}
              </label>
              <select
                id="countryId"
                name="countryId"
                className={`ad-select ${errors.countryId ? 'ad-input-error' : ''}`}
                value={formData.countryId}
                onChange={handleChange}
              >
                <option value="">{t('area.create.fields.country')}</option>
                {countries.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.nameEng}
                  </option>
                ))}
              </select>
              {errors.countryId && (
                <span className="ad-error-text">{errors.countryId}</span>
              )}
            </div>
          </div>

          <div className="ad-form-section">
            <div className="ad-check-row">
              <input
                id="isActive"
                type="checkbox"
                name="isActive"
                className="ad-checkbox"
                checked={formData.isActive}
                onChange={handleChange}
              />
              <label htmlFor="isActive">{t('area.create.fields.isActive')}</label>
            </div>
          </div>
        </div>

        <div className="ad-form-foot">
          <button type="button" className="ad-btn" onClick={() => navigate('/area')}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="ad-btn ad-btn-primary" disabled={loading}>
            {loading ? t('common.loading') : t('area.create.save')}
          </button>
        </div>
      </form>

      <SuccessModal
        show={showSuccessModal}
        message={t('area.create.messages.createSuccess')}
        onClose={() => setShowSuccessModal(false)}
      />
      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default AreaCreate;
